/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { readOnly, alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';
import { joinWordsAsSnippets } from 'cio/utils/join-words';
import { IdentifierKey, isAttributeEnabledIdentifier } from 'cio/utils/identifiers';

interface PersonAttributesEditorArgs {
  onReset: () => unknown;
  onSave: (attributes: Record<string, any>, hiddenAttributes: string[]) => unknown;
  proxy: Record<string, any>;
  personIsNew?: boolean;
  isSaving?: boolean;
  sentAttributes?: string[];
  isObject?: boolean;
}

const ENABLE_SEARCH_AFTER = 10;

export default class PersonAttributesEditor extends Component<PersonAttributesEditorArgs> {
  @service environment!: EnvironmentService;

  // hiddenAttrs is used to remove attributes.
  // They are set to empty strings on serialization.
  // See: serializers/customer.js
  @alias('args.proxy.hiddenAttrs') hiddenAttrs!: string[];
  @alias('args.proxy.currentAttrs') currentAttrs!: Record<string, any>[];
  @readOnly('args.proxy.isDirty') isDirty!: boolean;
  @readOnly('args.proxy.isInvalid') isInvalid!: boolean;

  @tracked isEmailChangeModalOpen = false;
  @tracked searchTerm = '';

  @computed('currentAttrs.[]')
  get mappableAttrs() {
    return this.currentAttrs.filter((attr) => {
      if (this.args.personIsNew) {
        return (
          !(this.environment.current.isEmailIdentified && attr.key === IdentifierKey.Id) &&
          attr.key !== IdentifierKey.CioId
        );
      }

      return true;
    });
  }

  get enabledIdentifierSnippets() {
    if (this.environment.current.isEmailIdentified) {
      return `<code>${IdentifierKey.Email}</code>`;
    }

    return joinWordsAsSnippets(this.environment.current.enabledIdentifiers);
  }

  get isSearchEnabled() {
    return !this.args.personIsNew && this.mappableAttrs.length >= ENABLE_SEARCH_AFTER;
  }

  get filteredAttrs() {
    if (!this.searchTerm) {
      return this.mappableAttrs;
    }

    return this.mappableAttrs.filter((attr) => attr.key.includes(this.searchTerm));
  }

  get identifierAttributes() {
    return this.filteredAttrs.filter((attr) =>
      isAttributeEnabledIdentifier(attr.key, this.environment.current.identifiers),
    );
  }

  get otherAttributes() {
    return this.filteredAttrs.filter(
      (attr) => !isAttributeEnabledIdentifier(attr.key, this.environment.current.identifiers),
    );
  }

  @action
  reset() {
    this.args.proxy.reset();
    return this.args.onReset();
  }

  @action
  save() {
    // If the user is on an email workspace, and the person isn't new, and the email has been changed, then we need to
    // show them a confirmation modal since changing the email is the equivalent to changing the ID on a person in an
    // ID identified workspace.
    if (
      this.environment.current.isEmailIdentified &&
      !this.args.personIsNew &&
      this.args.proxy.findAttrByKey('email').isValueChanged &&
      !this.isEmailChangeModalOpen
    ) {
      this.isEmailChangeModalOpen = true;
      return Promise.reject();
    }

    this.isEmailChangeModalOpen = false;

    let attributes = this.args.proxy.getModelAttributes();

    return this.args.onSave(attributes.attrs, attributes.hiddenAttrs);
  }

  @action
  removeAttribute(attribute: Record<string, any>) {
    this.args.proxy.remove(attribute);
  }

  @action
  addAttribute(key: string) {
    this.args.proxy.add(key);
  }

  @action
  createAttribute() {
    this.args.proxy.create();
  }

  @action
  hideAttribute(attribute: Record<string, any>) {
    this.args.proxy.hideAttribute(attribute);
  }

  @action
  toggleEmailChangeModal() {
    this.isEmailChangeModalOpen = !this.isEmailChangeModalOpen;
  }

  @action
  discardAndCloseEmailChangeModal() {
    this.reset();
    this.isEmailChangeModalOpen = !this.isEmailChangeModalOpen;
  }
}
